import { sanitizeInput } from '../../../utils/sanitize'
import { GMXCarousel } from '../../core/carousel/carousel'
import { calculateImwidthForImage } from '../../../utils/calculateImwidthForImage'
import { getEntryPresetParams } from '../../../utils/entry-presets'

import { VehicleModel } from '../../../models/vehicle-model.model'

import { TrimModel, VehicleTrimLinksModel } from '../../../models/vehicle-trim.model'

interface CTA {
	ctaKey: string
	label: string
	icon?: string
}

interface Trim {
	vehicleId: string
	tagline?: string
}

interface TrimFilter {
	trims: Trim[]
}

type CarouselSegmentImgBlobModel = Record<string, Record<string, Blob>>

/**
 * Carousel - Vehicle Trims By Model
 */
export class GMXCarouselVehicleTrimsByModel extends HTMLElement {
	modelScrollerEl: HTMLElement
	modelCarouselEl: GMXCarousel
	modelInfoContainerEl: HTMLElement
	vehicleNameEl: HTMLElement
	vehicleTrimNameEl: HTMLElement
	vehicleDescriptionEl: HTMLElement
	vehiclePriceEl: HTMLElement
	vehicleAsShownPriceEl: HTMLElement | null = null
	vehicleStartingAtPriceEl: HTMLElement | null = null
	ctaContainerEl: HTMLElement
	intersectionObserver: IntersectionObserver
	scrollerSlides: NodeListOf<HTMLElement>
	carouselControlsEl: HTMLElement

	private boundHandleObservationCallback = this.handleObservation.bind(this)
	private boundModelScrollerScrollHandler = this._handleModelScrollerScroll.bind(this)
	private boundCarouselControlsClickHandler = this._handleCarouselControlsClick.bind(this)

	/**
	 * Holds the data for the trims
	 * @private
	 */
	private modelsJSON: VehicleModel[]

	private activeModelId: string
	private activeTrimId: string

	private modelTrimImgBlobData: CarouselSegmentImgBlobModel = {}

	get modelApiEndpoint(): string {
		return this.getAttribute('data-models-api-endpoint')
	}

	get trimDataEndpointUrl(): string {
		return this.getAttribute('data-trims-data-endpoint')
	}

	get labelData(): Record<string, string> {
		return this.hasAttribute('data-labels') ? JSON.parse(this.getAttribute('data-labels')) : {}
	}

	get ctaLinks(): CTA[] {
		return this.hasAttribute('data-ctas') ? JSON.parse(this.getAttribute('data-ctas')) : []
	}

	get models(): Record<string, TrimFilter> {
		let models = {}

		try {
			models = JSON.parse(this.getAttribute('data-models'))
		} catch (error) {
			window.console.error(`ERROR: Vehicle Trims By Model - Failed to get models: ${error}`)
		}

		return models
	}

	get priceDisclosureLink(): string {
		return this.getAttribute('data-price-disclosure-link') || ''
	}
	set priceDisclosureLink(value: string) {
		this.setAttribute('data-price-disclosure-link', value)
	}

	connectedCallback(): void {
		this.modelScrollerEl = this.querySelector('.vehicle-scroller')
		this.modelInfoContainerEl = this.querySelector('.vehicle-info-container')
		this.vehicleNameEl = this.querySelector('.vehicle-name')
		this.vehicleDescriptionEl = this.querySelector('.vehicle-description')
		this.vehiclePriceEl = this.querySelector('.vehicle-price')
		this.ctaContainerEl = this.querySelector('.cta-container')
		this.carouselControlsEl = this.querySelector('.gmx-headline-controls')

		this.intersectionObserver = new IntersectionObserver(this.boundHandleObservationCallback, {
			root: null,
			rootMargin: '500px',
			threshold: 0,
		})
		this.intersectionObserver.observe(this)
	}

	disconnectedCallback(): void {
		if (this.modelCarouselEl) {
			this.modelCarouselEl.removeEventListener('transitionend', this.boundModelScrollerScrollHandler)
		}

		if (this.carouselControlsEl) {
			this.carouselControlsEl.removeEventListener('click', this.boundCarouselControlsClickHandler)
		}

		if (this.intersectionObserver) {
			this.intersectionObserver.disconnect()
		}
	}

	/**
	 * Callback when the IntersectionObserver triggers on the component
	 * @param entries
	 */
	handleObservation(entries: IntersectionObserverEntry[]): void {
		entries.forEach(entry => {
			if (entry.isIntersecting) {
				this.initCarousel()

				this.intersectionObserver.unobserve(this)
			}
		})
	}

	/**
	 * Init the Carousel when the component inits and the IntersectionObserver hits
	 */
	async initCarousel(): Promise<void> {
		// First, get the compare trim data JSON
		this.modelsJSON = await this.getModelData(this.modelApiEndpoint)

		if (this.modelsJSON.length) {
			const configuredModelCodes = Object.keys(this.models)
			const modelCodes = this.modelsJSON.map(m => m.code).filter(code => configuredModelCodes.includes(code))

			if (modelCodes.length) {
				await this._setTrimsForModels(modelCodes)

				this.createScroller()

				void this._processImages()

				// Preset the in-view slide from `?model=`/`?trim=` entry parameters, if it matches a slide.
				this._preselectFromEntryParams()

				// Initialize by calling the handle scroll event
				this._handleModelScrollerScroll()

				if (this.carouselControlsEl) {
					this.carouselControlsEl.addEventListener('click', this.boundCarouselControlsClickHandler)
				}
			}
		}
	}

	/**
	 * Get Vehicle Models by the given endpoint provided
	 * @param url
	 */
	async getModelData(url: string): Promise<VehicleModel[]> {
		let retModelData: VehicleModel[] = []

		try {
			const response = await fetch(url)

			if (response.ok) {
				const data = await response.json()
				retModelData = Array.isArray(data?.data?.models) ? data.data.models : []
			} else {
				throw new Error(`return status: ${response.status}`)
			}
		} catch (error) {
			window.console.error(`ERROR:  Failed to retrieve model data ${error}`)
		}

		return retModelData
	}

	/**
	 * Get all the trims for each model passed in and set the model.trim
	 * @param models
	 */
	async _setTrimsForModels(models: string[]): Promise<void> {
		const promises: Promise<void>[] = []

		models.forEach(modelCode => {
			promises.push(
				fetch(`${this.trimDataEndpointUrl}&model=${modelCode}`)
					.then(response => response.json())
					.then(response => {
						const trims: TrimModel[] = response?.data?.trims || []
						const model = this.modelsJSON.filter((m: VehicleModel) => m.code === modelCode)[0]
						const modelConfig = this.models[modelCode]

						if (!model || !modelConfig) {
							return
						}

						if (!model.trims?.length) {
							model.trims = []
						}

						;(modelConfig.trims || []).forEach(t => {
							const matchedTrim = trims.filter(mt => mt.code === t.vehicleId)[0]

							if (matchedTrim) {
								model.trims.push(matchedTrim)
							}
						})
					})
					.catch(error => {
						window.console.error(
							`ERROR:  Carousel Vehicle Trims By Model.  Failed to get trims for model ${modelCode}: ${error}`
						)
					})
			)
		})

		await Promise.all(promises)
	}

	/**
	 * Creates the main scroller element
	 */
	createScroller(): void {
		this.modelCarouselEl = document.createElement('gmx-carousel') as GMXCarousel
		this.modelCarouselEl.setAttribute('data-offset-w-peek', 'true')

		// First, build the slide collection for the carousel and then append
		const carouselSlideCollectionEl = document.createElement('div')
		carouselSlideCollectionEl.classList.add('slides-collection')
		this.modelCarouselEl.innerHTML = ''
		this.modelCarouselEl.appendChild(carouselSlideCollectionEl)

		for (const [key, value] of Object.entries(this.models)) {
			const trims = value?.trims
			trims.forEach(configuredTrim => {
				if (!configuredTrim?.vehicleId) {
					window.console.error(
						`ERROR: Carousel Vehicle Trims By Model. Invalid trim configuration for model ${key}. Expected at least one trim with a vehicleId.`
					)
					return
				}

				const modelData = this.modelsJSON.find(m => m.code === key)
				const activeTrim = modelData?.trims?.find(t => t.code === configuredTrim.vehicleId)

				if (activeTrim) {
					const asShownImage =
						activeTrim.images.find(img => img.imageID === 'default_image') || activeTrim.images[0]

					if (!asShownImage) {
						window.console.error(
							`ERROR: Carousel Vehicle Trims By Model. No Image for model ${key} / trim ${activeTrim.code} ; Expected image.`
						)
						return
					}

					// First create new slide element
					const slideEl = document.createElement('div')
					slideEl.classList.add('slide')
					slideEl.setAttribute('data-model-id', sanitizeInput(modelData.code))

					slideEl.setAttribute('data-active-trim-id', sanitizeInput(configuredTrim.vehicleId))
					slideEl.setAttribute('data-as-shown-price', asShownImage?.imageAsShownPrice || '')

					if (configuredTrim?.tagline) {
						let taglineExtraText = activeTrim.extraText.find(
							et => et.extraTextKey === 'trimByModelCarouselTagline'
						)

						if (!taglineExtraText) {
							taglineExtraText = {
								extraTextKey: 'trimByModelCarouselTagline',
								extraTextValue: '',
							}
							activeTrim.extraText.push(taglineExtraText)
						}

						taglineExtraText.extraTextValue = configuredTrim.tagline
					}

					carouselSlideCollectionEl.appendChild(slideEl)
				} else {
					window.console.error(
						`ERROR: Carousel Vehicle Trims By Model.  Failed to find trim data for model ${key} trim ${configuredTrim.vehicleId}`
					)
				}
			})
		}

		this.modelScrollerEl.appendChild(this.modelCarouselEl)
		this.scrollerSlides = this.modelScrollerEl.querySelectorAll('.slide')

		this.modelCarouselEl.addEventListener('transitionend', this.boundModelScrollerScrollHandler)
	}

	/**
	 * Presets the in-view slide from the `?model=`/`?trim=` entry parameters (by stable model/trim
	 * code), matching on both when provided, without reordering the carousel. Falls back to default
	 * behavior when parameters are absent or do not resolve to a slide.
	 */
	private _preselectFromEntryParams(): void {
		const { model: presetModel, trim: presetTrim } = getEntryPresetParams()

		if ((!presetModel && !presetTrim) || !this.modelCarouselEl) {
			return
		}

		const targetIndex = Array.from(this.scrollerSlides).findIndex(slide => {
			const modelMatches = !presetModel || slide.getAttribute('data-model-id') === presetModel
			const trimMatches = !presetTrim || slide.getAttribute('data-active-trim-id') === presetTrim
			return modelMatches && trimMatches
		})

		if (targetIndex > 0) {
			this.modelCarouselEl.goToSlide(targetIndex)
		}
	}

	/**
	 * Callback to handle the scroll event for the Carousel.  Will 'activate' the trim within the viewport
	 * @private
	 */
	private _handleModelScrollerScroll(): void {
		const componentClientRect = this.getBoundingClientRect()

		/* Loop through slides and find the one that fits within the tile */
		this.scrollerSlides.forEach(slide => {
			const slideClientRect = slide.getBoundingClientRect()

			if (
				slideClientRect.left >= componentClientRect.left &&
				slideClientRect.right <= componentClientRect.right
			) {
				const slideModelId = slide.getAttribute('data-model-id')
				const slideTrimId = slide.getAttribute('data-active-trim-id')

				if (slideTrimId !== this.activeTrimId) {
					const activeModel = this.modelsJSON.filter(model => model.code === slideModelId)[0]
					this.activeModelId = slideModelId

					const activeTrim = activeModel.trims.filter(trim => trim.code === slideTrimId)[0]
					this.activeTrimId = slideTrimId

					this.vehicleNameEl.innerText = `${activeModel.year} ${activeModel.name} `

					this.vehicleTrimNameEl = this.vehicleNameEl.querySelector('.vehicle-trim-name')

					if (!this.vehicleTrimNameEl) {
						this.vehicleTrimNameEl = document.createElement('span')
						this.vehicleTrimNameEl.classList.add('vehicle-trim-name')
						this.vehicleNameEl.appendChild(this.vehicleTrimNameEl)
					}

					this.vehicleTrimNameEl.textContent = activeTrim.title

					const trimTagline =
						activeTrim.extraText.filter(et => et.extraTextKey === 'trimByModelCarouselTagline')[0]
							?.extraTextValue || ''

					this.vehicleDescriptionEl.innerHTML =
						window.gmxDOMPurify?.sanitize?.(trimTagline) || sanitizeInput(trimTagline)

					this.ctaContainerEl.innerHTML = this._buildSlideCTAList(activeTrim.links)

					this.vehicleAsShownPriceEl = this.vehiclePriceEl.querySelector('.vehicle-as-shown')

					// Build starting at price with disclosure
					if (activeTrim.price) {
						const startingAtPriceDisclosureEl = document.createElement('span')
						startingAtPriceDisclosureEl.setAttribute('data-legal-uri', this.priceDisclosureLink)
						startingAtPriceDisclosureEl.textContent = activeTrim.price

						const existingStartingAtEl: HTMLElement =
							this.vehiclePriceEl.querySelector('.vehicle-starting-at')
						if (existingStartingAtEl) {
							this.vehicleStartingAtPriceEl = existingStartingAtEl
						} else {
							this.vehicleStartingAtPriceEl = document.createElement('span')
							this.vehicleStartingAtPriceEl.classList.add('vehicle-starting-at')
							if (this.vehicleAsShownPriceEl) {
								this.vehiclePriceEl.insertBefore(
									this.vehicleStartingAtPriceEl,
									this.vehicleAsShownPriceEl
								)
							} else {
								this.vehiclePriceEl.appendChild(this.vehicleStartingAtPriceEl)
							}
						}

						this.vehicleStartingAtPriceEl.innerText = `${this.labelData?.i18n_startingAt || ''} `
						this.vehicleStartingAtPriceEl.appendChild(startingAtPriceDisclosureEl)
					} else {
						if (this.vehicleStartingAtPriceEl) {
							this.vehicleStartingAtPriceEl.remove()
							this.vehicleStartingAtPriceEl = null
						}
					}

					// Build as shown price with disclosure
					const asShownPrice = slide.getAttribute('data-as-shown-price') || ''

					if (asShownPrice) {
						const asShownPriceDisclosureEl = document.createElement('span')
						asShownPriceDisclosureEl.setAttribute('data-legal-uri', this.priceDisclosureLink)
						asShownPriceDisclosureEl.textContent = asShownPrice

						if (!this.vehicleAsShownPriceEl) {
							this.vehicleAsShownPriceEl = document.createElement('span')
							this.vehicleAsShownPriceEl.classList.add('vehicle-as-shown')
							this.vehiclePriceEl.appendChild(this.vehicleAsShownPriceEl)
						}

						this.vehicleAsShownPriceEl.innerText = `${this.labelData?.i18n_asShown || ''} `
						this.vehicleAsShownPriceEl.appendChild(asShownPriceDisclosureEl)
					} else {
						if (this.vehicleAsShownPriceEl) {
							this.vehicleAsShownPriceEl.remove()
							this.vehicleAsShownPriceEl = null
						}
					}
				}
			}
		})
	}

	/**
	 * Build CTA Markup for Model Slide
	 * @private
	 */
	private _buildSlideCTAList(trimCTAs: VehicleTrimLinksModel[]): string {
		const ctaContainerEl = document.createElement('div')

		// Get the number of matched CTAs so we know if to use a secondary or tertiary button for CTAs beyond the first
		const numMatchedCTAs = trimCTAs.filter(cta => this.ctaLinks.map(cl => cl.ctaKey).includes(cta.linkID)).length

		let numCTAsAdded = 0
		this.ctaLinks.forEach((link: CTA) => {
			const trimLink = trimCTAs.filter(cta => cta.linkID === link.ctaKey)[0]

			if (trimLink) {
				const ctaEl: HTMLButtonElement | HTMLAnchorElement =
					trimLink.primaryLink_type === 'fragment'
						? document.createElement('button')
						: document.createElement('a')
				ctaEl.classList.add(
					'gmx-btn',
					numCTAsAdded === 0
						? 'gmx-btn-primary'
						: numMatchedCTAs === 2
							? 'gmx-btn-secondary'
							: 'gmx-btn-tertiary'
				)

				ctaEl.textContent = trimLink.primaryLink_labelOverride || link.label || 'Label Missing'

				if (link.icon) {
					ctaEl.classList.add('icon-left')
					ctaEl.dataset.iconName = link.icon
					ctaEl.dataset.iconPosition = 'before'
				}

				switch (trimLink.primaryLink_type) {
					case 'internal':
						;(ctaEl as HTMLAnchorElement).href = encodeURI(`${trimLink.primaryLink_internalUrl}`)
						;(ctaEl as HTMLAnchorElement).target = sanitizeInput(
							trimLink.primaryLink_internalTarget || '_self'
						)
						break
					case 'external':
						;(ctaEl as HTMLAnchorElement).href = encodeURI(`${trimLink.primaryLink_externalUrl}`)
						;(ctaEl as HTMLAnchorElement).target = sanitizeInput(
							trimLink.primaryLink_externalTarget || '_blank'
						)
						break
					case 'relative':
						;(ctaEl as HTMLAnchorElement).href = encodeURI(`${trimLink.primaryLink_relativeUrl}`)
						;(ctaEl as HTMLAnchorElement).target = sanitizeInput(
							trimLink.primaryLink_relativeTarget || '_self'
						)
						break
					case 'anchor':
						;(ctaEl as HTMLAnchorElement).href = `#${sanitizeInput(trimLink.primaryLink_anchor)}`
						break
					case 'fragment':
						;(ctaEl as HTMLButtonElement).type = 'button'

						if (trimLink.primaryLink_xfType === 'expanded-tile') {
							// Is an overlay
							ctaEl.dataset.overlayRevealId = trimLink.primaryLink_xfPath
							ctaEl.dataset.overlayType = trimLink.primaryLink_xfType
						} else {
							// Is a panel
							ctaEl.dataset.panelRevealId = trimLink.primaryLink_xfPath
							ctaEl.dataset.panelType = trimLink.primaryLink_xfType

							if (ctaEl.dataset.panelType === 'form-panel' && trimLink.vehicleSelectorModelYear) {
								ctaEl.dataset.panelData = JSON.stringify({
									vehicleSelectorYear: trimLink.vehicleSelectorModelYear,
									vehicleSelectorModel: trimLink.vehicleSelectorModelName || '',
								})
							}
						}
						break
					default:
						break
				}

				ctaContainerEl.appendChild(ctaEl)
				numCTAsAdded++
			}
		})

		return ctaContainerEl.innerHTML
	}

	/**
	 * Cycle through each model and trim and pull the image blob data for each trim image so that we can add to the carousel as they are loaded without having to wait for all images to load before showing anything to the user
	 * @private
	 */
	private async _processImages(): Promise<void> {
		// cycle through models and pull all the trim images and add one by one to the carousel as they are loaded so the user doesn't have to wait for all images to load before seeing anything
		const promises: Promise<void>[] = []
		for (const [key, value] of Object.entries(this.models)) {
			value.trims.forEach(trimCode => {
				this.modelTrimImgBlobData[key] = this.modelTrimImgBlobData[key] || {}
				this.modelTrimImgBlobData[key][trimCode.vehicleId] = null
				const modelData = this.modelsJSON.filter(m => m.code === key)[0]
				const trimData = modelData?.trims?.filter(t => t.code === trimCode.vehicleId)[0]
				const trimImages = trimData?.images
				const trimImg = trimImages?.find(img => img.imageID === 'default_image') || trimImages?.[0]

				if (trimImg) {
					const slideWidth = this.modelCarouselEl.querySelector('.slide')?.clientWidth || this.clientWidth
					const requestImgUrl = trimImg.imageURL.match(/\?.*$/)
						? `${trimImg.imageURL}&imwidth=${calculateImwidthForImage(slideWidth)}`
						: `${trimImg.imageURL}?imwidth=${calculateImwidthForImage(slideWidth)}`

					promises.push(
						fetch(requestImgUrl, {
							headers: {
								Accept: 'image/avif,image/webp,image/apng,*/*',
							},
						})
							.then(response => {
								if (!response.ok) {
									window.console.error(
										`ERROR: Carousel Vehicle Trims By Model.  Response NOT Ok.  Failed to load image for model ${key} trim ${trimCode.vehicleId}: ${response.status}`
									)
								}
								return response.blob()
							})
							.then(blob => {
								if (blob) {
									this.modelTrimImgBlobData[key][trimCode.vehicleId] = blob

									const currentSlide = [...this.scrollerSlides].find(
										slide =>
											slide.getAttribute('data-model-id') === key &&
											slide.getAttribute('data-active-trim-id') === trimCode.vehicleId
									)

									if (
										currentSlide &&
										currentSlide.getAttribute('data-model-id') === key &&
										currentSlide.getAttribute('data-active-trim-id') === trimCode.vehicleId
									) {
										const newImgForSlide = document.createElement('img') as HTMLImageElement

										try {
											const imgURL = window.URL.createObjectURL(blob)
											newImgForSlide.onload = () => {
												window.URL.revokeObjectURL(imgURL)
											}
											newImgForSlide.onerror = () => {
												window.URL.revokeObjectURL(imgURL)
												window.console.error(
													`ERROR: Carousel Vehicle Trims By Model.  Failed to load image for model ${key} trim ${trimCode.vehicleId}`
												)
											}

											newImgForSlide.src = imgURL
										} catch {
											window.console.warn(
												`WARN:  Carousel Vehicle Trims By Model.  Failed to create object URL for model ${key} trim ${trimCode.vehicleId} URL ${blob}`
											)
										}

										newImgForSlide.alt = sanitizeInput(trimData?.title || '')

										if (trimImg?.imageAsShownPrice) {
											currentSlide.setAttribute('data-as-shown-price', trimImg.imageAsShownPrice)
										}
										currentSlide.innerHTML = ''
										currentSlide.appendChild(newImgForSlide)
									}
								}
							})
							.catch(error => {
								window.console.error(
									`ERROR: Carousel Vehicle Trims By Model.  Failed to load image for model ${key} trim ${trimCode.vehicleId}: ${error}`
								)
							})
					)
				}
			})
		}

		await Promise.allSettled(promises)
	}

	/**
	 * Callback to handle when the carousel controls (prev & next buttons/arrows) are clicked to move the carousel backwards or forwards.
	 * @param e
	 */
	private _handleCarouselControlsClick(e: Event): void {
		const target = e.target as HTMLElement

		if (target.classList.contains('gmx-prev-ctrl')) {
			this.modelCarouselEl.activatePreviousSlide()
		} else if (target.classList.contains('gmx-next-ctrl')) {
			this.modelCarouselEl.activateNextSlide()
		}
	}
}

if (!customElements.get('gmx-carousel-vehicle-trims-by-model')) {
	customElements.define('gmx-carousel-vehicle-trims-by-model', GMXCarouselVehicleTrimsByModel)
}
